import { cn } from "@tcg/ui";
import { useLiveQuery } from "dexie-react-hooks";
import { Monitor, Printer, ScanLine, Tag } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuthState, useCan, usePos, useRegister, useStaff, registerKey } from "@/app/context";
import { ApprovalCancelled, useApproval } from "@/app/approval";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useConnectivity } from "@/features/offline/connectivity";
import { useChangeLanguage } from "@/hooks/useChangeLanguage";
import { isPosLocale, POS_LOCALE_NAMES, POS_LOCALES } from "@/i18n";
import { call } from "@/lib/api";
import { readSetting, writeSetting } from "@/lib/db";
import { env } from "@/lib/env";
import { errorText } from "@/lib/errors";
import { minutesSince } from "@/lib/snapshot";
import { PICKUP_SOUND_SETTING } from "@/realtime/pickups";
import { useQueryClient } from "@tanstack/react-query";

import { LabelPrinterSettings } from "./LabelPrinterSettings";
import { PrinterSettings } from "./PrinterSettings";
import { ScannerTest } from "./ScannerTest";

type Tab = "device" | "printer" | "scanner" | "labels";

const TABS: Array<{ id: Tab; icon: typeof Monitor }> = [
  { id: "device", icon: Monitor },
  { id: "printer", icon: Printer },
  { id: "scanner", icon: ScanLine },
  { id: "labels", icon: Tag },
];

/**
 * 設定 of this tablet (T07-34, design/05 §3.8, §6) — set up by the shop without calling anyone:
 * 端末 (the register, the tablet, app version, connection, the offline copy's age, the language of
 * the signed-in staff member, sound, ¥2,000 row), レシートプリンター, スキャナー (test + end key),
 * ラベルプリンター (+ print a card's label again). Everything is kept on this tablet (Dexie
 * `settings`); a manager (`settings.write`) can also save the printers as the register's setup for
 * every tablet of this counter (`PATCH /admin/pos/registers/{id}`).
 */
export function SettingsScreen() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("device");
  return (
    <div className="grid h-full min-h-0 grid-cols-[14rem_minmax(0,1fr)]">
      <nav aria-label={t("settings.title")} className="flex flex-col gap-1 border-r bg-muted p-2">
        <h1 className="px-2 py-3 text-2xl font-semibold">{t("settings.title")}</h1>
        {TABS.map(({ id, icon: Icon }) => (
          <button
            key={id}
            type="button"
            aria-current={tab === id ? "page" : undefined}
            onClick={() => setTab(id)}
            className={cn(
              "flex min-h-pos-touch items-center gap-2 rounded-md px-3 text-left text-lg",
              tab === id ? "bg-primary text-primary-foreground" : "hover:bg-background",
            )}
          >
            <Icon aria-hidden className="size-6" />
            {t(`settings.tab_${id}`)}
          </button>
        ))}
      </nav>
      <div className="min-h-0 overflow-y-auto p-6">
        {tab === "device" ? <DeviceSettings /> : null}
        {tab === "printer" ? <PrinterSettings /> : null}
        {tab === "scanner" ? <ScannerTest /> : null}
        {tab === "labels" ? <LabelPrinterSettings /> : null}
      </div>
    </div>
  );
}

function DeviceSettings() {
  const { t, i18n } = useTranslation();
  const { sounds, db, api, printer, labels } = usePos();
  const register = useRegister();
  const staff = useStaff();
  const state = useAuthState();
  const connectivity = useConnectivity();
  const canWrite = useCan("settings.write");
  const toast = useToast();
  const queryClient = useQueryClient();
  const [sound, setSound] = useState(sounds.enabled);
  const hide2000 = useLiveQuery(() => readSetting(db, "hideTwoThousand", false), [db]) ?? false;
  // T07-40: the chime when a web order is ready for pickup (受取)
  const pickupSound = useLiveQuery(() => readSetting(db, PICKUP_SOUND_SETTING, true), [db]) ?? true;
  const meta = useLiveQuery(() => db.snapshotMeta.toCollection().last(), [db]);
  const copyMinutes = minutesSince(meta?.fetchedAt);
  const device = state.kind === "locked" || state.kind === "unlocked" ? state.device : null;
  const reg = register.data?.register;

  const changeLanguage = useChangeLanguage();

  const saveForRegister = async () => {
    if (!reg) {
      return;
    }
    const p = printer.config;
    const l = labels.config;
    try {
      const saved = await call(() =>
        api.PATCH("/v1/admin/pos/registers/{register_id}", {
          params: { path: { register_id: reg.id } },
          body: {
            receiptPrinter: {
              connection: p.connection === "mock" ? "none" : p.connection,
              paperMm: p.paperWidth,
              ...(p.bridgeUrl ? { address: p.bridgeUrl } : {}),
              language: "escpos",
            },
            labelPrinter: {
              connection: l.connection === "mock" || l.connection === "pdf" ? "none" : l.connection,
              paperMm: null,
              ...(l.bridgeUrl ? { address: l.bridgeUrl } : {}),
              language: l.language,
            },
            cashDrawer: p.drawer,
            version: reg.version,
          },
        }),
      );
      void queryClient.invalidateQueries({ queryKey: registerKey });
      toast.show(t("settings.saved_register", { name: saved.name }), "success");
    } catch (error) {
      toast.show(errorText(error, t), "error");
    }
  };

  return (
    <section aria-labelledby="device-title" className="flex flex-col gap-5">
      <h2 id="device-title" className="text-2xl font-semibold">
        {t("settings.tab_device")}
      </h2>
      <dl className="grid max-w-2xl grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-lg">
        <dt className="text-muted-foreground">{t("settings.register")}</dt>
        <dd>{reg ? `${reg.code} ${reg.name}` : "—"}</dd>
        <dt className="text-muted-foreground">{t("settings.device")}</dt>
        <dd>{device?.deviceName ?? "—"}</dd>
        <dt className="text-muted-foreground">{t("settings.app_version")}</dt>
        <dd>{env.VITE_APP_VERSION}</dd>
        <dt className="text-muted-foreground">{t("settings.connection_state")}</dt>
        <dd>
          {connectivity.state === "online"
            ? t("status.online")
            : connectivity.state === "offline_long"
              ? t("offline.status_long")
              : t("offline.status_short")}
        </dd>
        <dt className="text-muted-foreground">{t("settings.copy")}</dt>
        <dd>
          {copyMinutes === null
            ? t("offline.copy_none")
            : t("settings.copy_age", { minutes: copyMinutes, count: meta?.count ?? 0 })}
        </dd>
      </dl>
      <label className="flex max-w-xs flex-col gap-1">
        <span className="font-medium">
          {staff ? t("settings.language_for", { name: staff.name }) : t("app.language")}
        </span>
        <select
          value={isPosLocale(i18n.resolvedLanguage) ? i18n.resolvedLanguage : "ja"}
          onChange={(e) => isPosLocale(e.target.value) && changeLanguage(e.target.value)}
          className="min-h-pos-touch rounded-md border px-3 text-lg"
        >
          {POS_LOCALES.map((code) => (
            <option key={code} value={code} lang={code}>
              {POS_LOCALE_NAMES[code]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          checked={sound}
          onChange={(e) => {
            setSound(e.target.checked);
            sounds.setEnabled(e.target.checked);
            void writeSetting(db, "sound", e.target.checked);
          }}
          className="size-6"
        />
        {t("settings.sound")}
      </label>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          checked={pickupSound}
          onChange={(e) => void writeSetting(db, PICKUP_SOUND_SETTING, e.target.checked)}
          className="size-6"
        />
        {t("settings.pickup_sound")}
      </label>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="checkbox"
          checked={hide2000}
          onChange={(e) => void writeSetting(db, "hideTwoThousand", e.target.checked)}
          className="size-6"
        />
        {t("settings.hide_2000")}
      </label>
      <div className="flex flex-wrap gap-3">
        {canWrite ? (
          <button
            type="button"
            onClick={() => void saveForRegister()}
            disabled={!reg}
            className="min-h-pos-touch rounded-md border border-primary px-6 text-lg"
          >
            {t("settings.save_register")}
          </button>
        ) : (
          <p className="text-muted-foreground">{t("settings.register_manager_only")}</p>
        )}
        <UnpairButton />
      </div>
    </section>
  );
}

/**
 * 登録解除 (T07-34 review 2026-09-28): a wrong tap takes the register out of use until an
 * administrator pairs it again — so only a manager may do it (`pos.devices.pair` /
 * `settings.write`, or the manager's PIN on this tablet), in the app's own dialog with one
 * sentence on what happens, and a warning while offline sales still wait to be sent.
 */
export function UnpairButton() {
  const { t } = useTranslation();
  const { auth, db } = usePos();
  const { ask } = useApproval();
  const canPair = useCan("pos.devices.pair");
  const canSettings = useCan("settings.write");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const waiting = useLiveQuery(() => db.opQueue.count(), [db]) ?? 0;

  const confirm = async () => {
    setBusy(true);
    try {
      if (!canPair && !canSettings) {
        const state = auth.getSnapshot();
        await ask({
          permission: "pos.devices.pair",
          action: "device.unpair",
          subject: state.kind === "unlocked" ? state.device.registerId : "register",
          reason: t("settings.unpair_manager"),
        });
      }
      setOpen(false);
      await auth.unpair(null);
    } catch (error) {
      if (!(error instanceof ApprovalCancelled)) {
        throw error;
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ml-auto min-h-pos-touch rounded-md border border-status-problem px-6 text-lg text-pos-problem"
      >
        {t("settings.unpair")}
      </button>
      <Modal
        open={open}
        title={t("settings.unpair_title")}
        description={t("settings.unpair_consequence")}
        onClose={() => setOpen(false)}
        busy={busy}
        footer={
          <>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="min-h-pos-touch rounded-md border px-6 text-lg"
            >
              {t("settings.unpair_keep")}
            </button>
            <button
              type="button"
              onClick={() => void confirm()}
              disabled={busy}
              className="min-h-pos-touch rounded-md bg-status-problem px-6 text-lg text-white"
            >
              {t("settings.unpair_confirm")}
            </button>
          </>
        }
      >
        {waiting > 0 ? (
          <p
            role="alert"
            className="rounded-md border border-status-problem p-3 text-lg text-pos-problem"
          >
            {t("settings.unpair_queue", { count: waiting })}
          </p>
        ) : null}
        {!canPair && !canSettings ? (
          <p className="text-muted-foreground">{t("settings.unpair_manager")}</p>
        ) : null}
      </Modal>
    </>
  );
}
